import { useAuthStore } from "@/core/stores/authStore";
import {
  FEATURE_PERMISSIONS,
  type FeaturePermissionKey,
  type PermissionTuple,
} from "./featurePermissions";

export type PermissionCanFn = (
  domain: string,
  action: string,
  resource: string,
) => boolean;

const defaultCan: PermissionCanFn = (domain, action, resource) =>
  useAuthStore.getState().can(domain, action, resource);

const featurePermissionMap = new Map<
  FeaturePermissionKey,
  PermissionTuple
>(
  Object.entries(FEATURE_PERMISSIONS) as Array<
    [FeaturePermissionKey, PermissionTuple]
  >,
);

const resolveTuple = (key: FeaturePermissionKey): PermissionTuple =>
  featurePermissionMap.get(key)!;

export const canFeature = (
  featureKey: FeaturePermissionKey,
  can: PermissionCanFn = defaultCan,
): boolean => {
  const tuple = resolveTuple(featureKey);
  return can(tuple.domain, tuple.action, tuple.resource);
};

export const canAny = (
  featureKeys: readonly FeaturePermissionKey[],
  can: PermissionCanFn = defaultCan,
): boolean => featureKeys.some((featureKey) => canFeature(featureKey, can));

export const canAll = (
  featureKeys: readonly FeaturePermissionKey[],
  can: PermissionCanFn = defaultCan,
): boolean => featureKeys.every((featureKey) => canFeature(featureKey, can));

/**
 * Raw Keycloak role-name check (e.g. `BOX_AFRICA_ADMIN`), for menu/route
 * items reserved to a persona that the domain/action/resource permission
 * catalog does not represent. That persona uses `hasRole` on the backend
 * too (see IdentityPolicyController/ScreeningPolicyController), not the
 * granular RBAC matrix.
 */
export const hasAnyRoleLabel = (
  roleLabels: readonly string[],
  hasRoleLabel: (label: string) => boolean = (label) =>
    useAuthStore.getState().hasRoleLabel(label),
): boolean => roleLabels.some((label) => hasRoleLabel(label));
